import { MatchUnitSize } from '@uefadigital/panenka';
import { PkButton, PkIcon } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import type { MouseEvent } from 'react';
import { memo, useMemo } from 'react';

import Image from '../../Image';
import { useTranslateFn } from '../../Translate';

import type { EditorialContent } from '@cobalt/utilities';
import { getVideoUrl } from '@services/LinkRuleService';
import type { Match } from '@shared-types/match.types';
import GoogleTagManagerHelper from '@tracking/gtm-helper';
import { getMatchTracking } from '@utilities/match-tracking';

import * as styles from './styles.module.scss';

dayjs.extend(duration);

const Highlight = ({
  match,
  size,
  video,
  disableHighLightIconClick,
}: {
  match: Match;
  size: MatchUnitSize;
  video: EditorialContent;
  disableHighLightIconClick?: boolean;
}) => {
  const t = useTranslateFn();
  const horizontal = useMemo(() => [MatchUnitSize.M, MatchUnitSize.XL].includes(size), [size]);
  const videoDuration = useMemo(() => !!video.duration && dayjs.duration(video.duration, 's').format('mm:ss'), [video]);
  const isPlusSize = useMemo(() => [MatchUnitSize.L_PLUS, MatchUnitSize.S_PLUS].includes(size), [size]);

  const ctaTracking = useMemo(() => getMatchTracking(match, true, 'highlights button'), [match]);
  const trackCtaClick = (e: MouseEvent | CustomEvent) => {
    if (!ctaTracking || (e as MouseEvent).button > 1) return;
    e.stopPropagation();
    GoogleTagManagerHelper.gtmCardElementClick(e.target as HTMLElement, ctaTracking);
  };

  if (isPlusSize)
    return (
      <a
        className={clsx('pk-w--100', 'pk-d--flex', styles.container)}
        slot='highlight-cta'
        onClick={trackCtaClick}
        onAuxClick={trackCtaClick}
        href={getVideoUrl(video.id)}
        title={video.title}
        target='_blank'
        pk-theme='dark'
      >
        <div
          className={clsx(
            'pk-position--absolute',
            'pk-bottom--0',
            'pk-m--xs2',
            'pk-d--flex',
            'pk-align-items--center',
            'pk-font--base-semibold',
            'pk-text--text-01',
            'pk-font-size--xs',
            styles.duration,
          )}
        >
          <PkIcon className='pk-mr--xs3' name='av-play-circle-negative' color='interaction-high-contrast' size={20} />
          {videoDuration}
        </div>
        <Image className='pk-rounded--m pk-w--100' src={video.imageUrl} alt={video.title} />
      </a>
    );

  return (
    <PkButton
      slot='cta'
      appearance='text'
      className='mu__highlights video-available'
      title={video.title}
      type='button'
      {...(!disableHighLightIconClick && {
        onPkClick: trackCtaClick,
        onAuxClick: trackCtaClick,
        linkTarget: '_blank',
        linkHref: getVideoUrl(video.id),
      })}
    >
      <PkIcon size='16' name='av-play-circle-negative' color='interaction' />
      {size !== MatchUnitSize.S && (
        <span className='text-highlights'>{t(horizontal ? 'watch_highlights' : 'highlights')}</span>
      )}
    </PkButton>
  );
};

export default memo(Highlight);
